import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  CheckBadgeIcon,
  ClockIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface AnimatedCounterProps {
  target: number;
  suffix?: string;
  duration?: number;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ target, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, target, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const stats = [
  {
    icon: GlobeAltIcon,
    end: 140,
    suffix: '+',
    label: 'Countries Served',
    accent: 'from-[#385CF2] to-[#0E9ED5]',
  },
  {
    icon: ClockIcon,
    end: 18,
    suffix: '+',
    label: 'Years Experience',
    accent: 'from-[#0E9ED5] to-[#385CF2]',
  },
  {
    icon: CheckBadgeIcon,
    end: 99,
    suffix: '%',
    label: 'Success Rate',
    accent: 'from-[#385CF2] to-[#0E9ED5]',
  },
];

const clientLogos = [
  { name: 'IATA', file: 'iata-logo.png' },
  { name: 'FIATA', file: 'fiata-logo-300dpi.jpg' },
  { name: 'SLA', file: 'singapore-logistic.png' },
  { name: 'bizSAFE', file: 'bizsafe.png' },
  { name: 'ISO 14001', file: 'iso-14001.svg' },
  { name: 'Cyber Essentials Mark', file: 'cem.svg' },
];

const Stats: React.FC = () => {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-14 lg:py-20">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-[#385CF2]/8 blur-[120px]" />
        <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-[#0E9ED5]/8 blur-[140px]" />
      </div>

      <div className="container-custom relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75 }}
          className="mx-auto max-w-3xl text-center"
        >
          <h2 className="text-slate-950">A Decade of Making Global ICT Logistics Feel Effortless.</h2>
        </motion.div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="rounded-[28px] border border-slate-200/70 bg-white p-8 text-center shadow-[0_24px_60px_-40px_rgba(15,23,42,0.24)]"
            >
              <div className={`inline-flex rounded-2xl bg-gradient-to-br ${stat.accent} p-3 text-white`}>
                <stat.icon className="h-6 w-6" />
              </div>

              <p className="mt-6 font-display text-5xl font-bold text-slate-950 sm:text-6xl">
                <AnimatedCounter target={stat.end} suffix={stat.suffix} />
              </p>
              <p className="mt-3 text-lg font-semibold text-slate-900">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-14 text-center"
        >
          <h3 className="text-2xl font-semibold text-slate-950 sm:text-3xl">Accredited by</h3>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {clientLogos.map((logo, index) => (
              <motion.img
                key={logo.name}
                src={`${process.env.PUBLIC_URL}/assets/images/partners/${logo.file}`}
                alt={logo.name}
                title={logo.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="h-12 w-auto object-contain sm:h-14"
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;
